import { useEffect, useState, type ReactNode } from "react";
import { MessageCircle, X, Users, Bot } from "lucide-react";
import { useLandingContent } from "@/features/admin/content/landingContent";
import { fetchActiveCommercials, type Commercial } from "@/features/commercials/commercials";
import { waLink } from "../lib/waLink";

const WA_GREEN = "#25D366";
const initials = (name: string) =>
  name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function WhatsAppGlyph({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.044zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
    </svg>
  );
}

/**
 * Botón flotante de contacto (abajo a la derecha). Al tocarlo despliega tres
 * acciones: contactar a un comercial, WhatsApp directo y preguntar a Tecobot.
 */
export function ContactFab() {
  const { contact } = useLandingContent();
  const [open, setOpen] = useState(false);
  const [commercialsOpen, setCommercialsOpen] = useState(false);
  const [commercials, setCommercials] = useState<Commercial[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!commercialsOpen || loaded) return;
    let active = true;
    fetchActiveCommercials()
      .then((d) => active && setCommercials(d))
      .catch(() => {})
      .finally(() => active && setLoaded(true));
    return () => {
      active = false;
    };
  }, [commercialsOpen, loaded]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setCommercialsOpen(false);
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <div className="fixed bottom-5 right-5 z-[90] flex flex-col items-end gap-3">
        {open && (
          <div className="flex flex-col items-end gap-2">
            <Action
              icon={<Users className="size-4" />}
              label="Contactar a un comercial"
              onClick={() => {
                setCommercialsOpen(true);
                setOpen(false);
              }}
            />
            {contact?.whatsapp && (
              <Action
                icon={<WhatsAppGlyph className="size-4" />}
                label="Contactar directo por WhatsApp"
                href={waLink(contact.whatsapp, contact.whatsappMessage)}
                accent={WA_GREEN}
              />
            )}
            <Action
              icon={<Bot className="size-4" />}
              label="Preguntar a Tecobot"
              href={contact?.tecobotUrl || undefined}
              disabled={!contact?.tecobotUrl}
            />
          </div>
        )}

        <div className="relative">
          {!open && (
            <>
              <span className="pointer-events-none absolute inset-0 rounded-full bg-primary/60 animate-pulse-ring" aria-hidden />
              <span className="pointer-events-none absolute inset-0 rounded-full bg-primary/40 animate-pulse-ring [animation-delay:1.2s]" aria-hidden />
            </>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Cerrar" : "Contáctanos"}
            className="relative size-14 grid place-items-center rounded-full bg-primary text-white shadow-xl shadow-primary/30 hover:scale-105 active:scale-95 transition-transform"
          >
            {open ? <X className="size-6" /> : <MessageCircle className="size-6" />}
          </button>
        </div>
      </div>

      {/* Modal: comerciales certificados */}
      {commercialsOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCommercialsOpen(false)} />
          <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl border border-black/5 overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-black/5">
              <div>
                <h3 className="font-bold text-lg">Comerciales certificados</h3>
                <p className="text-xs text-muted-foreground">Escríbele directo por WhatsApp.</p>
              </div>
              <button onClick={() => setCommercialsOpen(false)} className="grid size-8 place-items-center rounded-full hover:bg-neutral-100" aria-label="Cerrar">
                <X className="size-4" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-3">
              {!loaded ? (
                <p className="p-6 text-center text-sm text-muted-foreground">Cargando…</p>
              ) : commercials.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">Pronto tendremos comerciales disponibles.</p>
              ) : (
                <ul className="space-y-1">
                  {commercials.map((c) => (
                    <li key={c.id}>
                      <div className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-neutral-50">
                        {c.photo ? (
                          <img src={c.photo} alt={c.name} className="size-11 rounded-full object-cover border border-black/5" />
                        ) : (
                          <span className="grid size-11 place-items-center rounded-full bg-primary/10 text-primary text-sm font-bold">{initials(c.name)}</span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm truncate">{c.name}</p>
                          <p className="text-xs text-muted-foreground">Comercial certificado TecoPOS</p>
                        </div>
                        <a
                          href={waLink(c.whatsapp)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-white text-xs font-semibold hover:brightness-105 active:scale-95 transition-all shrink-0"
                          style={{ backgroundColor: WA_GREEN }}
                        >
                          <WhatsAppGlyph className="size-4" /> Chatear
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Action({
  icon,
  label,
  onClick,
  href,
  accent,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  href?: string;
  accent?: string;
  disabled?: boolean;
}) {
  const base =
    "inline-flex items-center gap-2.5 rounded-full bg-white pl-3 pr-4 py-2.5 text-sm font-semibold shadow-lg border border-black/5 hover:bg-neutral-50 active:scale-95 transition-all";
  const dot = (
    <span
      className="grid size-7 shrink-0 place-items-center rounded-full text-white"
      style={{ backgroundColor: accent ?? "var(--primary)" }}
    >
      {icon}
    </span>
  );
  if (href && !disabled) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={base}>
        {dot}
        {label}
      </a>
    );
  }
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} disabled:opacity-50`}>
      {dot}
      {label}
    </button>
  );
}
